import {Button} from '@/components/ui/button';
import {Modal} from '@/components/ui/modal';
import {Typography} from '@/components/ui/typography';
import {useAnalytics} from '@/hooks/useAnalytics';
import {
  useEventRegistrationActor,
  useEventRegistrationState,
} from '@/machines/eventRegistrationMachine/eventRegistrationContext';
import {
  getRegistrationPlatform,
  getSourceAttribution,
} from '@/utils/eventDisplayUtils';
import {ArrowRight} from 'lucide-react';
import {FC, useEffect, useRef} from 'react';

interface RegistrationHandoffModalProps {
  event: {
    id: string;
    name: string;
    sourceUrl: string;
    eventType: 'route' | 'race' | 'ride';
    externalId: string | null;
  };
}

/**
 * Post-track handoff modal. Reads open/closed state from the
 * `eventRegistrationMachine`'s `handoffModal` parallel region. Frames the
 * external registration as the next step in one continuous flow rather than
 * a separate destination, with a single CTA that opens the host site in a
 * new tab and dismisses the modal.
 *
 * Fires `'Handoff External Clicked'` before opening the new tab. Fires
 * `'Handoff Modal Dismissed'` when the modal closes WITHOUT a preceding
 * external click (i.e. the user closed it via the X / overlay / Escape).
 */
export const RegistrationHandoffModal: FC<RegistrationHandoffModalProps> = ({
  event,
}) => {
  const actor = useEventRegistrationActor();
  const state = useEventRegistrationState();
  const {track} = useAnalytics();
  const isOpen = state.matches({handoffModal: 'open'});

  const platformAnalytics =
    getRegistrationPlatform(event.externalId) ??
    getSourceAttribution(event.sourceUrl)?.name ??
    null;
  const platformDisplay = platformAnalytics ?? 'the host site';
  const ctaLabel = `Continue to ${platformDisplay}`;
  const participantsVerb =
    event.eventType === 'race' ? 'racing' : 'riding';

  const close = () => actor?.send({type: 'CLOSE_HANDOFF'});

  // Tracks whether the user clicked the external CTA during the current open
  // session. When true, suppress the dismissed event on close. Reset on each
  // open transition so a re-open after a non-interactive close starts fresh.
  const interactedRef = useRef(false);
  // Tracks the previous `isOpen` so the close-effect can detect open → closed
  // transitions and avoid firing on the initial render.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) interactedRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen && !interactedRef.current) {
      track('Handoff Modal Dismissed', {
        event_id: event.id,
        event_type: event.eventType,
        platform: platformAnalytics,
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, event.id, event.eventType, platformAnalytics, track]);

  const handleCta = () => {
    track('Handoff External Clicked', {
      event_id: event.id,
      event_type: event.eventType,
      platform: platformAnalytics,
    });
    interactedRef.current = true;
    window.open(event.sourceUrl, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Almost there."
    >
      <div className="flex flex-col gap-4">
        <Typography level="B1" className="text-neutral-700">
          You're tracking <strong>{event.name}</strong> on Wayra. The last step
          is registering with the host
          {platformAnalytics ? (
            <>
              {' '}on <strong>{platformDisplay}</strong>
            </>
          ) : null}
          .
        </Typography>
        <Button onClick={handleCta} className="w-full">
          {ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </Button>
        <Typography level="B3" className="text-neutral-600 text-center">
          We'll remind you, surface updates, and show you who else is{' '}
          {participantsVerb}.
        </Typography>
      </div>
    </Modal>
  );
};
